import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ArrowLeft, Mail, MapPin, ShoppingBag, Repeat, BookOpen, Send, Gift, Newspaper, Pencil } from "lucide-react";
import { ClientInfoForm } from "../components/ClientInfoForm";

export function ClientLink({ email, children, className = "" }) {
  if (!email) return <span className={className}>{children || "—"}</span>;
  return (
    <Link data-testid={`client-link-${email}`} to={`/admin/clients/${encodeURIComponent(email)}`}
      className={`hover:text-[#A3876A] hover:underline underline-offset-2 ${className}`} title="Ouvrir la fiche cliente">
      {children || email}
    </Link>
  );
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";
const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

function Section({ icon: Icon, title, count, children, testId }) {
  return (
    <section data-testid={testId} className="bg-[#FDFBF7] border border-[#E8DCC8] p-5">
      <h2 className="font-serif text-xl text-[#2C2621] mb-3 flex items-center gap-2">
        <Icon size={16} className="text-[#A3876A]"/> {title}
        {count !== undefined && <span className="text-xs text-[#7C7064] font-sans">({count})</span>}
      </h2>
      {children}
    </section>
  );
}

const Empty = ({ children }) => <p className="text-sm text-[#7C7064] italic">{children}</p>;

const MC_STATUS = { active: "Active", cancel_scheduled: "Résiliation programmée", canceled: "Annulée", payment_failed: "Paiement échoué",
  pending: "En attente", gift_pending: "Cadeau en attente", gift_active: "Cadeau en cours", gift_ended: "Cadeau terminé" };

export default function AdminClient() {
  const { email } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  async function changeEmail() {
    const nv = window.prompt("Nouvelle adresse email de la cliente :", d?.email || "");
    if (!nv || nv.trim().toLowerCase() === d.email) return;
    const resend = window.confirm("Renvoyer à la nouvelle adresse les emails liés à ses achats (confirmation, bienvenue, facture) ?");
    try {
      const r = await api.post(`/admin/clients/${encodeURIComponent(d.email)}/change-email`, { new_email: nv.trim(), resend });
      toast.success(`Email corrigé · ${Object.keys(r.data.updated).length} fiche(s) mise(s) à jour · ${r.data.resent.filter(x => x.ok).length} email(s) renvoyé(s)`);
      navigate(`/admin/clients/${encodeURIComponent(r.data.new_email)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(false);
  const reload = () => api.get(`/admin/clients/${encodeURIComponent(email)}`).then(r => setD(r.data)).catch(e => setErr(e.response?.data?.detail || "Erreur"));
  useEffect(() => { setD(null); setEditing(false); reload(); }, [email]); // eslint-disable-line

  if (err) return <p className="text-[#A86962]">{err}</p>;
  if (!d) return <p className="text-[#7C7064]">Chargement…</p>;
  const a = d.address;

  return (
    <div data-testid="admin-client-page" className="max-w-6xl">
      <Link to="/admin/contacts" className="text-xs text-[#7C7064] hover:text-[#A3876A] inline-flex items-center gap-1 mb-4"><ArrowLeft size={12}/> Contacts</Link>
      <p className="font-hand text-xl text-[#A3876A] mb-1">Fiche cliente</p>
      <h1 data-testid="client-name" className="font-serif text-4xl mb-2">{d.name || d.email}</h1>
      <p className="text-sm text-[#4A4036] flex flex-wrap items-center gap-4 mb-8">
        <span className="inline-flex items-center gap-1"><Mail size={13} className="text-[#A3876A]"/> <a href={`mailto:${d.email}`} className="hover:underline">{d.email}</a>
          <button data-testid="client-change-email" onClick={changeEmail} className="ml-2 text-xs text-[#A3876A] hover:underline">Corriger l'email</button></span>
        {a && <span className="inline-flex items-center gap-1" data-testid="client-address"><MapPin size={13} className="text-[#A3876A]"/>
          {[a.line1, a.line2, `${a.postal_code || ""} ${a.city || ""}`.trim(), a.country].filter(Boolean).join(", ")}</span>}
        <button data-testid="client-edit-info" onClick={() => setEditing(v => !v)} className="inline-flex items-center gap-1 text-xs text-[#A3876A] hover:underline"><Pencil size={12}/> Modifier les infos</button>
      </p>
      {editing && <ClientInfoForm client={d} onDone={() => { setEditing(false); reload(); }} onCancel={() => setEditing(false)}/>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[["Total payé", fmtEur(d.total_paid)], ["Paiements", d.payments.length],
          ["Club de Lettres", d.mailclub.length ? (MC_STATUS[d.mailclub[0].status] || d.mailclub[0].status) : "—"],
          ["Programme", d.participant ? `Semaine ${Math.max(d.participant.last_sent_week || 0, d.participant.current_week || 0)}/52` : "—"]].map(([l, v]) => (
          <div key={l} className="bg-white border border-[#E8DCC8] p-4">
            <p className="text-[10px] uppercase tracking-wider text-[#7C7064]">{l}</p>
            <p className="font-serif text-2xl text-[#2C2621] mt-1">{v}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section icon={ShoppingBag} title="Achats & paiements" count={d.payments.length} testId="client-payments">
          {d.payments.length === 0 ? <Empty>Aucun paiement.</Empty> : (
            <table className="w-full text-sm">
              <tbody>{d.payments.map((p, i) => (
                <tr key={`${p.date}-${p.source}-${p.amount}-${i}`} className="border-t border-[#E8DCC8]">
                  <td className="py-2 text-xs text-[#7C7064] w-24">{fmtDate(p.date)}</td>
                  <td className="py-2">{p.label}</td>
                  <td className="py-2 text-right font-serif">{fmtEur(p.amount / 100)}</td>
                </tr>))}
              </tbody>
            </table>
          )}
        </Section>

        <Section icon={Repeat} title="Club de Lettres" count={d.mailclub.length} testId="client-mailclub">
          {d.mailclub.length === 0 ? <Empty>Pas d'abonnement Club de Lettres.</Empty> : d.mailclub.map((m, i) => (
            <div key={m._prepaid_id || m._gift_id || m.stripe_subscription_id || `${m.email}-${i}`} className="text-sm mb-3 last:mb-0">
              <p><strong>{MC_STATUS[m.status] || m.status}</strong>{m.is_gift && <span className="text-xs text-[#A3876A] ml-2">🎁 {m.gift_months_remaining ?? 0}/{m.gift_months_total} mois · offert par {m.gift_buyer_name}</span>}</p>
              <p className="text-xs text-[#7C7064]">Depuis le {fmtDate(m.activated_at || m.created_at)} · dernier paiement {fmtDate(m.last_payment_at)} · prochain {fmtDate(m.current_period_end)}</p>
              {Object.keys(m.mailings || {}).length > 0 && (
                <p className="text-xs text-[#7C7064] mt-1">Enveloppes : {Object.entries(m.mailings).sort().map(([k, v]) => `${k} ${v.status === "sent" ? "✓" : "…"}`).join(" · ")}</p>
              )}
            </div>
          ))}
        </Section>

        <Section icon={BookOpen} title="Programme 52 semaines" count={d.programme_emails.length} testId="client-programme">
          {!d.participant ? <Empty>Pas inscrite au programme.</Empty> : (
            <>
              <p className="text-sm mb-2">{d.participant.programme_type === "epistolaire" ? "Épistolaire" : "Essentiel"} · {d.participant.status} · inscrite le {fmtDate(d.participant.created_at)}</p>
              <div className="flex flex-wrap gap-1">
                {d.programme_emails.slice().sort((x, y) => x.week - y.week).map(l => (
                  <span key={l.week} title={`${fmtDate(l.sent_at)}${l.opened_at ? " · ouvert" : ""}`}
                    className={`text-[11px] px-1.5 py-0.5 border ${l.opened_at ? "bg-[#7B8A72] text-white border-[#7B8A72]" : "bg-white border-[#E8DCC8] text-[#4A4036]"}`}>S{l.week}</span>
                ))}
              </div>
              <p className="text-[11px] text-[#7C7064] mt-2">Vert = email ouvert.</p>
            </>
          )}
        </Section>

        <Section icon={Send} title="Correspondances & coffrets" count={d.correspondances.length + d.coffrets.length} testId="client-correspondances">
          {d.correspondances.length + d.coffrets.length === 0 ? <Empty>Aucune correspondance.</Empty> : (
            <ul className="text-sm space-y-1">
              {d.correspondances.map(c => <li key={c.correspondance_id}>✉️ {c.product_type} · {c.status} · {fmtDate(c.created_at)}{c.amount_paid != null && ` · ${fmtEur(c.amount_paid / 100)}`}</li>)}
              {d.coffrets.map(c => <li key={c.coffret_id}>📦 Coffret {c.product_type} · {c.status === "shipped" ? `envoyé le ${fmtDate(c.shipped_at)}` : "à envoyer"}</li>)}
            </ul>
          )}
        </Section>

        <Section icon={Gift} title="Cartes cadeaux" count={d.gift_cards_bought.length + d.gift_cards_used.length} testId="client-gifts">
          {d.gift_cards_bought.length + d.gift_cards_used.length === 0 ? <Empty>Aucune carte cadeau.</Empty> : (
            <ul className="text-sm space-y-1">
              {d.gift_cards_bought.map(g => <li key={g.code}>Offerte à {g.recipient_name || "—"} · <span className="font-mono text-xs">{g.code}</span> · {g.used_at ? "utilisée" : g.payment_status}</li>)}
              {d.gift_cards_used.map(g => <li key={g.code}>Reçue de {g.buyer_name || "—"} · <span className="font-mono text-xs">{g.code}</span> · utilisée le {fmtDate(g.used_at)}</li>)}
            </ul>
          )}
        </Section>

        <Section icon={Newspaper} title="Newsletter & emails reçus" count={d.newsletter_sends.length} testId="client-newsletter">
          <p className="text-sm mb-2">{d.newsletter ? `Inscrite à la newsletter depuis le ${fmtDate(d.newsletter.created_at)}` : "Non inscrite à la newsletter"}{d.lead && " · a reçu le cadeau de bienvenue"}</p>
          {d.newsletter_sends.length > 0 && (
            <ul className="text-xs text-[#4A4036] space-y-1">
              {d.newsletter_sends.map((s, i) => <li key={`${s.sent_at}-${i}`}>{fmtDate(s.sent_at)} — {s.subject || "newsletter"} <span className="text-[#7C7064]">({s.last_event})</span></li>)}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}
